import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, X } from 'lucide-react';
import ReportPageScaffold from '../ReportPageScaffold';
import { BlockLoader } from '../loading';
import DateRangeFilter from '../DateRangeFilter';
import { useTableSort, SortTh } from '../dashboard/useTableSort';
import { usePagination } from '../../hooks/usePagination';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { storeVisitTasksAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useLinkedSearch } from '../../hooks/useLinkedSearch';

/**
 * Answers captured during store visits: the "Questionnaire answers" tab of Reports ›
 * Store Visit Compliance. It was the whole page while the report was called Store Visit
 * Tasks, and is moved here unchanged; the shell (pages/StoreVisitTasksReportPage) now
 * draws the heading.
 *
 * The API returns one row per ANSWER; this page pivots them into one row per SUBMISSION,
 * the way the Task Response report does. Answers stay behind a drill-down rather than
 * becoming columns, because different forms ask different questions — spreading every
 * question of every form across one table would be mostly empty cells.
 */

interface AnswerRow {
  submission_id: string;
  form_id: string;
  form_name: string | null;
  question_id: string | null;
  question_text: string | null;
  question_type: string | null;
  display_order: number | null;
  answer_text: string | null;
  answer_value: unknown;
  photo_url: string | null;
  employee_code: string;
  employee_name: string | null;
  store_id: string | null;
  store_name: string | null;
  store_code: string | null;
  city: string | null;
  state: string | null;
  session_id: number | null;
  check_in_time: string | null;
  check_out_time: string | null;
  submitted_at: string;
}

interface Submission {
  submissionId: string;
  formId: string;
  formName: string;
  employeeCode: string;
  employeeName: string;
  storeName: string;
  storeCode: string;
  city: string;
  state: string;
  submittedAt: string;
  checkInTime: string | null;
  checkOutTime: string | null;
  answers: { questionId: string; questionText: string; questionType: string; order: number; value: string }[];
}

const isoDay = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDay(d);
};

/** A photo answer holds a blob path; the readable value is the path itself. */
const answerOf = (r: AnswerRow): string => {
  if (r.photo_url) return r.photo_url;
  if (r.answer_text != null && r.answer_text !== '') return r.answer_text;
  if (Array.isArray(r.answer_value)) return (r.answer_value as unknown[]).join(', ');
  if (r.answer_value != null) return String(r.answer_value);
  return '';
};

const fmtDateTime = (iso: string | null) =>
  !iso ? '' : new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });

export default function StoreVisitAnswersTab() {
  const projectId = useAuthStore((s) => s.selectedProjectId);

  const [startDate, setStartDate] = useState(daysAgo(29));
  const [endDate, setEndDate] = useState(isoDay(new Date()));
  const [appliedRange, setAppliedRange] = useState({ start: daysAgo(29), end: isoDay(new Date()) });
  const [rows, setRows] = useState<AnswerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A link can open the answers narrowed to one visitor or one store (?employeeCode=,
  // ?storeCode=), or to a questionnaire by its name (?q=).
  const [searchQuery, setSearchQuery] = useLinkedSearch();
  const [formFilter, setFormFilter] = useState('all');
  const [drill, setDrill] = useState<Submission | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const load = useCallback(async () => {
    if (!projectId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await storeVisitTasksAPI.report(projectId, appliedRange.start, appliedRange.end);
      setRows(res.data?.data ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not load the report.');
    } finally {
      setLoading(false);
    }
  }, [projectId, appliedRange]);

  useEffect(() => { load(); }, [load]);

  /** One row per submission, answers nested. */
  const submissions = useMemo<Submission[]>(() => {
    const bySubmission = new Map<string, Submission>();
    for (const r of rows) {
      let sub = bySubmission.get(r.submission_id);
      if (!sub) {
        sub = {
          submissionId: r.submission_id,
          formId: r.form_id,
          formName: r.form_name ?? 'Untitled form',
          employeeCode: r.employee_code,
          employeeName: r.employee_name || r.employee_code,
          storeName: r.store_name ?? '',
          storeCode: r.store_code ?? '',
          city: r.city ?? '',
          state: r.state ?? '',
          submittedAt: r.submitted_at,
          checkInTime: r.check_in_time,
          checkOutTime: r.check_out_time,
          answers: [],
        };
        bySubmission.set(r.submission_id, sub);
      }
      // A questionless form writes one sentinel row (question_id NULL) — it records that the
      // visit happened, so it must count as a submission but not as an answer.
      if (r.question_id) {
        sub.answers.push({
          questionId: r.question_id,
          questionText: r.question_text ?? 'Deleted question',
          questionType: r.question_type ?? 'text',
          order: r.display_order ?? 0,
          value: answerOf(r),
        });
      }
    }
    for (const sub of bySubmission.values()) sub.answers.sort((a, b) => a.order - b.order);
    return [...bySubmission.values()];
  }, [rows]);

  const formOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of submissions) if (!seen.has(s.formId)) seen.set(s.formId, s.formName);
    return [...seen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [submissions]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return submissions.filter((s) => {
      if (formFilter !== 'all' && s.formId !== formFilter) return false;
      if (!q) return true;
      return (
        s.employeeName.toLowerCase().includes(q) ||
        s.employeeCode.toLowerCase().includes(q) ||
        s.storeName.toLowerCase().includes(q) ||
        s.storeCode.toLowerCase().includes(q) ||
        s.city.toLowerCase().includes(q) ||
        s.formName.toLowerCase().includes(q)
      );
    });
  }, [submissions, formFilter, searchQuery]);

  const sort = useTableSort<Submission>(filtered, {
    submitted: (s) => s.submittedAt,
    form: (s) => s.formName,
    visitor: (s) => s.employeeName,
    store: (s) => s.storeName,
    city: (s) => s.city,
    answers: (s) => s.answers.length,
  }, { defaultKey: 'submitted', defaultDir: 'desc' });

  const { totalPages, safePage, paginatedData } = usePagination(sort.sorted, currentPage, pageSize);
  useEffect(() => { setCurrentPage(1); }, [searchQuery, formFilter, sort.sortKey, sort.sortDir, appliedRange]);

  const hasPendingDateChanges = startDate !== appliedRange.start || endDate !== appliedRange.end;
  const applyDateRange = () => setAppliedRange({ start: startDate, end: endDate });

  /**
   * One form selected → WIDE (a column per question), which is what people actually pivot in
   * Excel. Mixed forms → LONG (a row per answer), because a wide sheet across forms is mostly
   * empty cells.
   */
  const handleExport = () => {
    const context = ['Submitted', 'Form', 'Visitor', 'Employee Code', 'Store', 'Store Code', 'City', 'State', 'Check In', 'Check Out'];
    const contextOf = (s: Submission) => [
      fmtDateTime(s.submittedAt), s.formName, s.employeeName, s.employeeCode,
      s.storeName, s.storeCode, s.city, s.state,
      fmtDateTime(s.checkInTime), fmtDateTime(s.checkOutTime),
    ];
    const single = formFilter !== 'all';
    let headers: string[];
    let csvRows: string[][];
    if (single) {
      const questions = new Map<string, { text: string; order: number }>();
      for (const s of sort.sorted) {
        for (const a of s.answers) if (!questions.has(a.questionId)) questions.set(a.questionId, { text: a.questionText, order: a.order });
      }
      const ordered = [...questions.entries()].sort((a, b) => a[1].order - b[1].order);
      headers = [...context, ...ordered.map(([, q]) => q.text)];
      csvRows = sort.sorted.map((s) => {
        const byQ = new Map(s.answers.map((a) => [a.questionId, a.value]));
        return [...contextOf(s), ...ordered.map(([id]) => byQ.get(id) ?? '')];
      });
    } else {
      headers = [...context, 'Question', 'Answer'];
      csvRows = sort.sorted.flatMap((s) =>
        s.answers.length === 0
          ? [[...contextOf(s), '', '']]
          : s.answers.map((a) => [...contextOf(s), a.questionText, a.value]),
      );
    }
    downloadCsv(`Store_visit_tasks_${appliedRange.start}_to_${appliedRange.end}.csv`, buildCsv(headers, csvRows));
  };

  if (!projectId) {
    return <div className="p-6 text-sm text-gray-500">Pick a project to see its store visit task answers.</div>;
  }

  return (
    <ReportPageScaffold
      embedded
      title="Questionnaire answers"
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      searchPlaceholder="Search by visitor, store, code, city…"
      searchInputPaddingRight="pr-4"
      filters={
        <>
          {formOptions.length > 0 && (
            <select
              value={formFilter}
              onChange={(e) => setFormFilter(e.target.value)}
              title="Narrow to one questionnaire — the export then gets a column per question"
              className={`rounded-md border px-3 py-2 text-sm font-medium shadow-sm ${
                formFilter !== 'all' ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <option value="all">All questionnaires</option>
              {formOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            hasPendingDateChanges={hasPendingDateChanges}
            onStartDateChange={(e) => setStartDate(e.target.value)}
            onEndDateChange={(e) => setEndDate(e.target.value)}
            onStartDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onEndDateKeyDown={(e) => { if (e.key === 'Enter' && hasPendingDateChanges) applyDateRange(); }}
            onApply={applyDateRange}
          />
          <button
            onClick={handleExport}
            disabled={sort.sorted.length === 0}
            className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Download
          </button>
        </>
      }
    >
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {loading ? (
        <BlockLoader className="py-16" />
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-gray-500">
          No store visit tasks were submitted in this range.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
                <tr>
                  <SortTh label="SUBMITTED" sortKey="submitted" sort={sort} className="whitespace-nowrap px-4 py-3" />
                  <SortTh label="QUESTIONNAIRE" sortKey="form" sort={sort} className="whitespace-nowrap px-4 py-3" />
                  <SortTh label="VISITOR" sortKey="visitor" sort={sort} className="whitespace-nowrap px-4 py-3" />
                  <SortTh label="STORE" sortKey="store" sort={sort} className="whitespace-nowrap px-4 py-3" />
                  <SortTh label="CITY" sortKey="city" sort={sort} className="whitespace-nowrap px-4 py-3" />
                  <SortTh label="ANSWERS" sortKey="answers" sort={sort} className="whitespace-nowrap px-4 py-3" />
                  <th className="whitespace-nowrap px-4 py-3 text-center">ACTION</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 bg-white">
                {paginatedData.map((s) => (
                  <tr key={s.submissionId} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">{fmtDateTime(s.submittedAt)}</td>
                    <td className="px-4 py-3 text-gray-900">{s.formName}</td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900">{s.employeeName}</div>
                      <div className="text-xs text-gray-400">{s.employeeCode}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900">{s.storeName || '—'}</div>
                      <div className="text-xs text-gray-400">{s.storeCode}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{s.city || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{s.answers.length}</td>
                    <td className="whitespace-nowrap px-4 py-3"><TableActionGroup>
                      <TableActionButton action="view" label="View store visit task"
                        onClick={() => setDrill(s)} />
                    </TableActionGroup></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
            <span>Showing {paginatedData.length} of {filtered.length}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40">Previous</button>
              <span>Page {safePage} of {totalPages}</span>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="rounded-md border border-gray-300 px-3 py-1.5 disabled:opacity-40">Next</button>
            </div>
          </div>
        </>
      )}

      {drill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm" onClick={() => setDrill(null)}>
          <div className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{drill.formName}</h2>
                <p className="text-xs text-gray-500">
                  {drill.employeeName} · {drill.storeName || 'Unknown store'} · {fmtDateTime(drill.submittedAt)}
                </p>
              </div>
              <button onClick={() => setDrill(null)} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              {drill.answers.length === 0 ? (
                <p className="text-sm text-gray-500">This questionnaire has no questions — the submission records that the visit happened.</p>
              ) : (
                <dl className="space-y-4">
                  {drill.answers.map((a) => (
                    <div key={a.questionId}>
                      <dt className="text-xs font-medium text-gray-500">{a.questionText}</dt>
                      <dd className="mt-0.5 text-sm text-gray-900 break-words">
                        {a.questionType === 'photo' && a.value
                          ? <span className="text-xs text-gray-600">Photo: {a.value}</span>
                          : (a.value || <span className="text-gray-400">—</span>)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </div>
        </div>
      )}
    </ReportPageScaffold>
  );
}
